import { Button, Flex, Kbd, Text } from '@radix-ui/themes';
import { toast } from 'react-hot-toast';
import { HotkeyCallback, useHotkeys } from 'react-hotkeys-hook';
import { OptionsOrDependencyArray } from 'react-hotkeys-hook/dist/types';
import { DrawMode, toggleDrawMode } from '@/entities/canvas';
import {
  HorizontalNavigation,
  captureBackground,
  captureFrame,
  deleteFrameImage,
  navigateFrame,
  selectIsControlsDisabled,
  setIsControlsDisabled,
} from '@/entities/collection';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

export function Footer() {
  const dispatch = useAppDispatch();
  const isBackgroundSelected = useAppSelector(
    (state) =>
      !!state.canvas.bgRectangle && !!state.canvas.bgRectangle.bottomRight
  );

  const isBackgroundDrawMode = useAppSelector(
    (state) => state.canvas.drawMode === DrawMode.Background
  );
  const isBackgroundSaving = useAppSelector(
    (state) => state.collection.pending === 'background'
  );
  const isFrameSaving = useAppSelector(
    (state) => state.collection.pending === 'frame'
  );
  const isDeleting = useAppSelector(
    (state) => state.collection.pending === 'delete'
  );
  const isBusy = isBackgroundSaving || isFrameSaving || isDeleting;
  const isControlsDisabled = useAppSelector(selectIsControlsDisabled);

  return (
    <footer>
      <Flex className="px-4 py-2 border-t border-gray-200" gap="4">
        {isBackgroundDrawMode ? (
          <>
            <Shortcut
              kbd="C"
              keys="c"
              label="Place a point"
              onFire={() => {}}
              disabled={isControlsDisabled}
            />
            <Shortcut
              kbd="S"
              keys="s"
              label={
                isBackgroundSaving ? 'Saving background...' : 'Save background'
              }
              disabled={isControlsDisabled}
              onFire={() => {
                if (isBusy) return;

                if (isBackgroundSelected) {
                  dispatch(captureBackground())
                    .unwrap()
                    .then(() => {
                      dispatch(toggleDrawMode());
                      toast.success('Background image is saved');
                    })
                    .catch(() => {
                      toast.error("Can't save background");
                    });
                } else {
                  toast.error(
                    'Include a 2 poitns (top-left bottom right)  to save a background image'
                  );
                }
              }}
            />
            <Shortcut
              kbd="Esc"
              keys="esc"
              label="Cancel background capture"
              onFire={() => {
                dispatch(toggleDrawMode());
              }}
              disabled={isControlsDisabled}
            />
          </>
        ) : (
          <>
            <Shortcut
              kbd="Ctrl + Shift + B"
              keys="ctrl+shift+b, meta+shift+b"
              label="Capture background"
              onFire={() => {
                dispatch(toggleDrawMode(DrawMode.Background));
              }}
              disabled={isControlsDisabled}
            />
            <Shortcut
              kbd="Space"
              keys="space"
              label={isFrameSaving ? 'Capturing...' : 'Capture frame'}
              onFire={() => {
                if (isBusy) return;

                dispatch(captureFrame());
              }}
              disabled={isControlsDisabled}
            />
            <Shortcut
              kbd="Ctrl + Shift + D"
              keys="ctrl+shift+d"
              label={isDeleting ? 'Deleting...' : 'Delete frame'}
              onFire={() => {
                if (isBusy) return;

                dispatch(deleteFrameImage());
              }}
              disabled={isControlsDisabled}
            />
            <Shortcut
              kbd="←/→"
              keys="left,right"
              label="Previous/Next frame"
              onFire={(_, handler) => {
                if (handler.keys?.[0]) {
                  const direction = handler.keys?.[0] as HorizontalNavigation;
                  dispatch(navigateFrame({ direction }));
                }

                return false;
              }}
              disabled={isControlsDisabled}
            />
          </>
        )}
        {import.meta.env.DEV && (
          <>
            <div className="grow"></div>
            <Button
              size="1"
              color="red"
              variant="soft"
              onClick={() => {
                dispatch(setIsControlsDisabled(!isControlsDisabled));
              }}
            >
              {isControlsDisabled ? 'Enable controls' : 'Disable controls'}
            </Button>
          </>
        )}
      </Flex>
    </footer>
  );
}

function Shortcut({
  kbd,
  label,
  keys,
  onFire,
  options = { preventDefault: true, enableOnFormTags: true },
  disabled,
}: {
  kbd: string;
  keys: string;
  label: string;
  onFire: HotkeyCallback;
  options?: OptionsOrDependencyArray;
  disabled?: boolean;
}) {
  useHotkeys(keys, onFire, { enabled: !disabled, ...options });

  return (
    <Flex gap="2" align="center" className={disabled ? 'opacity-40' : ''}>
      <Kbd className="font-medium">{kbd}</Kbd>
      <Text size="1">{label}</Text>
    </Flex>
  );
}
